import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { CtaButton } from './CtaButton';
import { PageLightbox } from './PageLightbox';
import { volumePreviewPages } from '../data/volumePreviewPages';
import { CHECKOUT_URL } from '../data/checkout';
const pages = volumePreviewPages;
export function VolumePreview() {
  const trackRef = useRef<HTMLUListElement>(null);
  const [active, setActive] = useState(0);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const getStep = () => {
    const track = trackRef.current;
    const first = track?.children[0] as HTMLElement | undefined;
    if (!track || !first) return 0;
    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    return first.offsetWidth + gap;
  };
  const updateActive = useCallback(() => {
    const track = trackRef.current;
    const step = getStep();
    if (!track || step === 0) return;
    const idx = Math.round(track.scrollLeft / step);
    setActive(Math.max(0, Math.min(idx, pages.length - 1)));
  }, []);
  useEffect(() => {
    updateActive();
    window.addEventListener('resize', updateActive);
    return () => window.removeEventListener('resize', updateActive);
  }, [updateActive]);
  const goTo = (i: number) => {
    const clamped = Math.max(0, Math.min(i, pages.length - 1));
    trackRef.current?.scrollTo({
      left: clamped * getStep(),
      behavior: 'smooth'
    });
  };
  return <section aria-labelledby="volume-preview-heading" className="w-full bg-white text-ink" data-id="element-343" data-mp="2e0rbgunxt2wc">
      <div className="max-w-6xl mx-auto px-5 py-16 sm:py-20" data-id="element-344" data-mp="2izkqwsmsb3z5">
        <div className="text-center" data-id="element-345" data-mp="2ny5pyoqavhm0">
          <p className="text-[11px] font-semibold uppercase tracking-[1.8px] text-coral-deep" data-id="element-346" data-mp="2nv037g558rtg">
            A look inside
          </p>
          <h2 id="volume-preview-heading" className="mt-3 font-display font-bold text-plum text-3xl sm:text-5xl" data-id="element-347" data-mp="tjlaqrkr0ea0">
            Real pages from Volume 4: Create &amp; Perform
          </h2>
          <p className="mx-auto mt-4 max-w-[60ch] text-base text-ink-soft" data-id="element-348" data-mp="20w3xq8rd0oed">
            Every week is a full 45-minute class in 4 pages: class plan,
            warm-up, combo card in 3 levels, and a game. All 6 volumes follow
            the same format.
          </p>
        </div>

        <div className="relative mt-10" data-id="element-349" data-mp="25tcfw9cicsfm">
          <ul ref={trackRef} onScroll={updateActive} aria-label="Sample pages from Volume 4" className="-mx-5 flex snap-x snap-mandatory items-center gap-4 overflow-x-auto py-6 [--w:min(78vw,39vh)] px-[calc(50%-var(--w)/2)] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden md:mx-0 md:gap-6 md:[--w:300px] lg:[--w:340px]" data-id="element-350" data-mp="25wi5dpp1twbv">
            {pages.map((page, i) => {
            const isActive = active === i;
            return <li key={page.src} className="w-[var(--w)] shrink-0 snap-center" aria-current={isActive ? 'true' : undefined} data-id="element-351" data-mp="32kxzlz33stvb" data-mpi={i}>
                  <button type="button" onClick={() => setLightboxIndex(i)} aria-label={`Open full screen: ${page.caption}`} className={`block w-full overflow-hidden rounded-2xl bg-cream shadow-card transition-[transform,opacity] duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] focus:outline-none focus-visible:ring-4 focus-visible:ring-gold/50 motion-reduce:transition-none ${isActive ? 'scale-100 opacity-100' : 'scale-[0.94] opacity-80 md:scale-[0.88]'}`} data-id="element-352" data-mp="3h86fhd7cxndm">
                    <img src={page.src} alt={page.caption} loading={i === 0 ? 'eager' : 'lazy'} width={1080} height={1920} draggable={false} className="aspect-[9/16] w-full object-contain" data-id="element-353" data-mp="2ogmp99ccf924" />
                  </button>
                </li>;
          })}
          </ul>

          <button type="button" onClick={() => goTo(active - 1)} disabled={active === 0} aria-label="Previous page" className="absolute left-0 top-1/2 hidden h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-plum text-white shadow-card transition-[background-color,opacity] duration-150 hover:bg-plum-mid focus:outline-none focus-visible:ring-4 focus-visible:ring-gold/50 disabled:pointer-events-none disabled:opacity-30 md:flex" data-id="element-354" data-mp="27h21lvgd7l6m">
            <ChevronLeftIcon className="h-6 w-6" aria-hidden="true" data-id="element-355" data-mp="1js99yb6rzzht" />
          </button>
          <button type="button" onClick={() => goTo(active + 1)} disabled={active === pages.length - 1} aria-label="Next page" className="absolute right-0 top-1/2 hidden h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-plum text-white shadow-card transition-[background-color,opacity] duration-150 hover:bg-plum-mid focus:outline-none focus-visible:ring-4 focus-visible:ring-gold/50 disabled:pointer-events-none disabled:opacity-30 md:flex" data-id="element-356" data-mp="1frh0bgtzrsl0">
            <ChevronRightIcon className="h-6 w-6" aria-hidden="true" data-id="element-357" data-mp="15ol59ld0yg1v" />
          </button>
        </div>

        <div className="mt-2 flex justify-center gap-1" role="tablist" aria-label="Choose page" data-id="element-358" data-mp="19pn5a4ermw4w">
          {pages.map((page, i) => <button key={page.src} type="button" role="tab" aria-selected={active === i} aria-label={`Go to page ${i + 1}: ${page.caption}`} onClick={() => goTo(i)} className="flex h-6 w-6 items-center justify-center rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-gold" data-id="element-359" data-mp="hvuvnislg8ur" data-mpi={i}>
              <span className={`block h-2 w-2 rounded-full transition-colors duration-200 ease-out ${active === i ? 'bg-coral' : 'bg-ink-muted/30'}`} data-id="element-360" data-mp="22bgd4sex50yj" />
            </button>)}
        </div>

        <div className="mt-8 flex flex-col items-center" data-id="element-361" data-mp="c8eczhzieaxy">
          <p className="text-center text-sm text-ink-soft" data-id="element-362" data-mp="c7waw4q612i4">
            Sample pages shown. 6 volumes, 40 weeks, 300 pages in total.
          </p>
          <div className="mt-4 w-full max-w-md" data-id="element-363" data-mp="3fov9rky9mvgn">
            <CtaButton href={CHECKOUT_URL} data-id="element-364" data-mp="3fpo6ru7fa6ll">Get Dance Class Ready</CtaButton>
          </div>
        </div>
      </div>

      <PageLightbox pages={pages} index={lightboxIndex} onClose={() => setLightboxIndex(null)} onChange={setLightboxIndex} data-id="element-365" data-mp="3anyvcdgafqwz" />
    </section>;
}